import { useCallback, useEffect, useState } from "react"
import { Ban, Loader2, MoreHorizontal } from "lucide-react"
import { transactionsAPI } from "../services/api"
import toast from "../utils/toast"

// Payments recorded on a job, with a per-row "Void payment" action.
// Voided payments stay in the list (struck through) so there's a history,
// but stop counting toward what's been paid (backend re-syncs invoice + job).
const money = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`

const methodLabel = (tx) => {
  const notes = String(tx.notes || "")
  if (/Stripe PaymentIntent/i.test(notes)) return "Card (Stripe)"
  const m = String(tx.payment_method || "").replace(/[_-]+/g, " ").trim()
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : "Payment"
}

const isCardCharge = (tx) =>
  /Stripe PaymentIntent/i.test(String(tx.notes || "")) || /stripe|card/i.test(String(tx.payment_method || ""))

export default function JobPaymentHistory({ jobId, reloadKey, onChanged }) {
  const [txs, setTxs] = useState([])
  const [loading, setLoading] = useState(true)
  const [menuFor, setMenuFor] = useState(null)
  const [voidingId, setVoidingId] = useState(null)

  const load = useCallback(async () => {
    if (!jobId) return
    try {
      const data = await transactionsAPI.getForJob(jobId)
      setTxs(data?.transactions || [])
    } catch {
      setTxs([])
    } finally {
      setLoading(false)
    }
  }, [jobId])

  useEffect(() => { load() }, [load, reloadKey])

  // Close the row menu on any outside click.
  useEffect(() => {
    if (menuFor == null) return
    const close = () => setMenuFor(null)
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [menuFor])

  const voidPayment = async (tx) => {
    setMenuFor(null)
    const msg = isCardCharge(tx)
      ? `Void this ${money(tx.amount)} card payment?\n\nIt stays in the payment history as "Voided" and the invoice goes back to unpaid.\n\nThis does NOT refund the card — refund it in your Stripe dashboard if the money should go back to the customer.`
      : `Void this ${money(tx.amount)} payment?\n\nIt stays in the payment history as "Voided" and the invoice goes back to unpaid.`
    if (!window.confirm(msg)) return
    setVoidingId(tx.id)
    try {
      const r = await transactionsAPI.void(tx.id)
      const state = r?.paymentState?.state
      toast(`Payment voided.${state ? ` Invoice is now ${state}.` : ""}`, "success")
      await load()
      onChanged?.()
    } catch (e) {
      toast(e?.response?.data?.message || e?.response?.data?.error || "Could not void the payment.", "error")
    } finally {
      setVoidingId(null)
    }
  }

  if (loading || txs.length === 0) return null

  return (
    <div className="mt-3 pt-3 border-t border-[var(--sf-border)]">
      <div className="text-[10.5px] text-[var(--sf-ink-3)] font-semibold uppercase tracking-wide mb-1.5">
        Payments
      </div>
      <ul className="space-y-1">
        {txs.map((tx) => {
          const voided = tx.status === "voided"
          const tip = parseFloat(tx.tip_amount) || 0
          return (
            <li key={tx.id} className="flex items-center gap-2 text-[12px]">
              <div className="min-w-0 flex-1">
                <div className={`font-semibold ${voided ? "text-[var(--sf-ink-3)] line-through" : "text-[var(--sf-ink)]"}`}>
                  {methodLabel(tx)}
                </div>
                <div className="text-[11px] text-[var(--sf-ink-3)]">
                  {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ""}
                  {tip > 0 ? ` · incl. ${money(tip)} tip` : ""}
                </div>
              </div>
              {voided && (
                <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[var(--sf-red-soft)] text-[var(--sf-red-dark)]">
                  Voided
                </span>
              )}
              <div
                className={`font-semibold ${voided ? "text-[var(--sf-ink-3)] line-through" : "text-[var(--sf-ink)]"}`}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {money(tx.amount)}
              </div>
              <div className="relative w-6 flex-shrink-0">
                {!voided && (
                  voidingId === tx.id ? (
                    <Loader2 size={14} className="animate-spin text-[var(--sf-ink-3)]" />
                  ) : (
                    <button
                      type="button"
                      aria-label="Payment actions"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setMenuFor(menuFor === tx.id ? null : tx.id)}
                      className="p-1 rounded hover:bg-[var(--sf-bg-hover)] text-[var(--sf-ink-3)]"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  )
                )}
                {menuFor === tx.id && (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 top-7 z-20 w-40 bg-[var(--sf-bg-card)] border border-[var(--sf-border)] rounded-md shadow-lg py-1"
                  >
                    <button
                      type="button"
                      onClick={() => voidPayment(tx)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50"
                    >
                      <Ban size={13} /> Void payment
                    </button>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
